import { useEffect } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, GeoJSON, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";

export const TURKEY_CENTER = [39.0, 35.0];
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

export const pinIcon = (color = "#059669") =>
  L.divIcon({
    className: "",
    html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
    popupAnchor: [0, -22],
  });

export const BaseTiles = () => <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />;

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng) });
  return null;
};

const Recenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 15));
  }, [position?.[0], position?.[1]]);
  return null;
};

const GeoCenter = ({ query, active }) => {
  const map = useMap();
  useEffect(() => {
    if (!active || !query?.trim()) return;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=tr&accept-language=tr&q=${encodeURIComponent(query)}`);
        const [hit] = await r.json();
        if (hit) map.setView([Number(hit.lat), Number(hit.lon)], query.includes(",") ? 13 : 11);
      } catch { /* best-effort */ }
    }, 700);
    return () => clearTimeout(t);
  }, [query, active]);
  return null;
};

export const LocationPicker = ({ lat, lng, onChange, query }) => {
  const position = lat != null && lng != null ? [lat, lng] : null;
  const pick = ({ lat: la, lng: ln }) => onChange(Number(la.toFixed(6)), Number(ln.toFixed(6)));
  return (
    <div data-testid="location-picker" className="h-64 rounded-lg overflow-hidden border border-slate-200 relative z-0">
      <MapContainer center={position || TURKEY_CENTER} zoom={position ? 15 : 5} className="h-full w-full" scrollWheelZoom>
        <BaseTiles />
        <ClickHandler onPick={pick} />
        <Recenter position={position} />
        <GeoCenter query={query} active={!position} />
        {position && (
          <Marker position={position} icon={pinIcon()} draggable eventHandlers={{ dragend: (e) => pick(e.target.getLatLng()) }} />
        )}
      </MapContainer>
    </div>
  );
};

export const PARCEL_STYLE = { color: "#047857", weight: 3, fillColor: "#10b981", fillOpacity: 0.18 };

export const ParcelShape = ({ geometry, fit = true, style = PARCEL_STYLE }) => {
  const map = useMap();
  const key = JSON.stringify(geometry?.coordinates?.[0]?.[0] || []);
  useEffect(() => {
    if (fit && geometry) map.fitBounds(L.geoJSON(geometry).getBounds(), { padding: [30, 30], maxZoom: 18 });
  }, [key]);
  return geometry ? <GeoJSON key={key} data={geometry} style={style} /> : null;
};

export const StaticLocationMap = ({ lat, lng, geometry }) => (
  <div data-testid="portfolio-location-map" className="h-48 rounded-lg overflow-hidden border border-slate-200 relative z-0">
    <MapContainer center={[lat, lng]} zoom={15} className="h-full w-full" scrollWheelZoom={false}>
      <BaseTiles />
      <ParcelShape geometry={geometry} />
      <Marker position={[lat, lng]} icon={pinIcon()} />
    </MapContainer>
  </div>
);
